
import { Button, Card, InputNumber, Message, Select, Space, Table } from "@arco-design/web-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Currency, Number } from "@/components/Numeric";
import { ColorfulTable } from "@/components/ColorfulTable";
import { PresetColorPicker } from "@/components/PresetColorPicker";
import { SOAPI } from "@/apis/sales";
import { FileUtil } from "@/utils";
import { UserProfile, UserProfileType } from "@/utils";
import dayjs from "dayjs";
import _ from "lodash";
import { SOStatusDef, SOStatus } from "@/enums/sales";
import { EnumLabel } from "@/components/Enum";
import Reference from "@/components/Reference";
import OpenSOReportByItem from "./item-report";
import { EditableNote } from "./note";
import { Link } from "react-router-dom";

const OPEN_STATUSES = [
    SOStatus.Created,
    SOStatus.Issued,
    SOStatus.Picked,
    SOStatus.Partial,
    SOStatus.PartialFulfilledInvoiced,
    SOStatus.FulfilledPartiallyInvoiced,
    SOStatus.PartiallyInvoiced
];

export const OpenSOReport = () => {
    const [totalOpenQantity, setTotalOpenQantity] = useState(0);
    const [totalOpenAmount, setTotalOpenAmount] = useState(0);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState();
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState()
    const [collapsed, setCollapsed] = useState(true);
    const [expandedItems, setExpandedItems] = useState();
    const [dueDateColor, setDueDateColor] = useState();
    const [dueDateDays, setDueDateDays] = useState();
    const [readyColor, setReadyColor] = useState();
    const [readyDays, setReadyDays] = useState();

    const columns = useMemo(() => [
        {
            title: "SO#",
            dataIndex: "soNo",
            render: (col, record) => <Link to={`/workspace/sales/so/${record.id}`}>{record.soNo}</Link>,
            sorter: (a, b) => a.id > b.id ? 1 : -1,
            width: 110
        }, {
            title: "Status",
            dataIndex: "status",
            render: (col, record) => <EnumLabel enumObj={SOStatusDef} value={record.status} />,
            width: 100
        }, {
            title: "Customer",
            dataIndex: "customerName",
        }, {
            title: "SO Date",
            dataIndex: "soDate",
            sorter: (a, b) => a.soDate > b.soDate ? 1 : -1,
            width: 105
        }, {
            title: <div>
                <span>Due Date</span>
                <div>
                    <InputNumber hideControl
                        placeholder="Days"
                        size="mini"
                        min={0}
                        style={{ width: 50 }}
                        value={dueDateDays}
                        onChange={(val) => setDueDateDays(val)} />
                    <PresetColorPicker
                        size="mini"
                        value={dueDateColor}
                        onChange={(color) => {
                            setDueDateColor(color);
                        }}
                    />
                </div>
            </div>,
            dataIndex: "dueDate",
            colorful: (record) => record.dueDate && dayjs(record.dueDate).isBefore(dayjs().add(dueDateDays ?? 0, 'day')),
            bgColor: dueDateColor,
            width: 110
        }, {
            title: <div>
                <span>Ready</span>
                <div>
                    <InputNumber hideControl
                        placeholder="Days"
                        size="mini"
                        min={0}
                        style={{ width: 50 }}
                        value={readyDays}
                        onChange={val => setReadyDays(val)} />
                    <PresetColorPicker
                        value={readyColor}
                        onChange={(color) => {
                            setReadyColor(color);
                        }}
                    />
                </div>
            </div>,
            dataIndex: "readyDate",
            colorful: (record) => record.readyDate && dayjs(record.readyDate).isBefore(dayjs().add(readyDays ?? 0, 'day')),
            bgColor: readyColor,
            width: 110
        }, {
            title: "Reference",
            render: (col, record) => <Reference value={record.reference} />,
            width: 150
        }, {
            title: "Open Amount",
            render: (col, record) => <Currency value={record.openAmount} />,
            width: 120
        }, {
            title: "Note",
            render: (col, record) => <EditableNote soId={record.id} value={record.note} />,
        }
    ], [readyColor, readyDays, dueDateColor, dueDateDays]);

    const getData = useCallback(async (customers, items) => {
        const result = await SOAPI.report({
            customers,
            items
        });
        const { data, totalQuantity, totalAmount } = result;
        setData(data);
        setTotalOpenQantity(totalQuantity);
        setTotalOpenAmount(totalAmount);
        setPagination((pagination) => ({
            ...pagination,
            total: data.length,
            current: 1,
        }));
    }, []);

    const saveProfile = () => {
        UserProfile.saveProfile(UserProfileType.OPEN_SO_REPORT, {
            collapsed,
            dueDateColor,
            dueDateDays,
            readyColor,
            readyDays,
        });
    }

    useEffect(() => {
        const getOpenCustomersAndItems = async () => {
            const customers = await SOAPI.openCustomers();
            const items = await SOAPI.openItems();
            const sortedCustomers = _.sortBy(customers, "customerName");
            const sortedItems = _.sortBy(items, "itemName");
            setCustomers(_.map(sortedCustomers, item => ({
                label: item.customerName,
                value: item.id
            })));
            setItems(_.map(sortedItems, item => ({
                label: item.itemName,
                value: item.id
            })));
        }
        getOpenCustomersAndItems();
    }, [])

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        const load = async () => {
            const profile = await UserProfile.getProfile(UserProfileType.OPEN_SO_REPORT);
            if (profile) {
                profile.dueDateColor && setDueDateColor(profile.dueDateColor);
                profile.dueDateDays && setDueDateDays(profile.dueDateDays);
                profile.readyColor && setReadyColor(profile.readyColor);
                profile.readyDays && setReadyDays(profile.readyDays);
                profile.collapsed && setCollapsed(profile.collapsed);
            }
        };
        load();
    }, []);

    const expandedRowRender = (record) => {
        return <Table rowKey="id"
            columns={[
                {
                    title: "Item Name",
                    dataIndex: "itemName",
                }, {
                    title: "Item Description",
                    dataIndex: "description",
                }, {
                    title: "SO Qty",
                    render: (col, record) => <Number value={record.quantity} />,
                }, {
                    title: "Open Qty",
                    render: (col, record) => <Number value={record.quantity - record.fulfilledQty} />,
                }, {
                    title: "Picked Qty",
                    render: (col, record) => <Number value={record.pickedQty} />,
                }, {
                    title: "Rate",
                    render: (col, record) => <Currency value={record.rate} />
                }, {
                    title: "Amount",
                    render: (col, record) => <Currency value={record.rate * (record.quantity - record.fulfilledQty)} />
                }
            ]}
            pagination={false}
            data={_.filter(record.items, (item) => item.quantity > item.fulfilledQty)} />;
    };

    return (
        <Card title="Open SO Report" extra={<Space>
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Select customers..."
                maxTagCount={1}
                style={{ width: 200 }}
                value={selectedCustomers}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                onChange={(value) => {
                    setSelectedCustomers(value);
                }}
                options={customers}
            />
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Select items..."
                maxTagCount={1}
                style={{ width: 200 }}
                value={selectedItems}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                onChange={(value) => {
                    setSelectedItems(value);
                }}
                options={items}
            />
            <Button type="primary" onClick={() => getData(selectedCustomers, selectedItems)}>Search</Button>
            <Button type="primary" onClick={() => {
                const state = !collapsed;
                setCollapsed(state);
                if (state) {
                    setExpandedItems([]);
                } else {
                    setExpandedItems(_.map(data, item => item.id));
                }
            }}>{collapsed ? "Expand" : "Collapse"}</Button>
            <Button type="primary" onClick={async () => {
                const res = await SOAPI.export({
                    status: OPEN_STATUSES,
                    customers: selectedCustomers,
                    items: selectedItems
                });
                FileUtil.download(res.data, "Open SO Report.xlsx");
            }}>Export</Button>
            <Button type="primary" onClick={async () => {
                await saveProfile();
                Message.success("Profile Saved");
            }}>Save Profile</Button>
        </Space>}>
            <Space>
                <span>Total Open Qty: <Number value={totalOpenQantity} /></span>
                <span>Total Open Amount: <Currency value={totalOpenAmount} /></span>
            </Space>
            <ColorfulTable rowKey="id"
                style={{ marginTop: 5 }}
                columns={columns}
                indentSize={60}
                expandedRowKeys={expandedItems}
                expandedRowRender={expandedRowRender}
                onExpandedRowsChange={(rows) => {
                    setExpandedItems(rows);
                }}
                scroll={{ y: 'calc(100vh - 320px)' }}
                pagination={pagination}
                data={data}
                onChange={(pagination) => {
                    setPagination(pagination);
                }}
            />
        </Card>
    );
}

OpenSOReport.ReportByItem = OpenSOReportByItem;